// Panel Cobros ML — lista ml_pagos con status, liberación de dinero y asiento Zeta
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const STATUS_META = {
  approved: { bg: '#DCFCE7', color: '#166534', label: 'Aprobado' },
  pending:  { bg: '#FEF3C7', color: '#92400E', label: 'Pendiente' },
  in_process: { bg: '#DBEAFE', color: '#1E40AF', label: 'En proceso' },
  rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rechazado' },
  refunded: { bg: '#F3E8FF', color: '#7C3AED', label: 'Devuelto' },
  cancelled:{ bg: '#F1F5F9', color: '#475569', label: 'Cancelado' },
}

function fmt(n, cur = 'UYU') { return `${cur === 'USD' ? 'U$S' : '$'} ${Number(n || 0).toLocaleString('es-UY')}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : '—' }
function diasHastaLiberacion(release) {
  if (!release) return null
  const dif = new Date(release).getTime() - Date.now()
  const dias = Math.round(dif / (1000 * 60 * 60 * 24))
  if (dias > 0) return `en ${dias}d`
  if (dias === 0) return 'hoy'
  return `hace ${Math.abs(dias)}d`
}

export default function CobrosML() {
  const [pagos, setPagos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroStatus, setFiltroStatus] = useState('todos')

  useEffect(() => { loadData() }, [])
  async function loadData() {
    setLoading(true)
    const { data: pagosData } = await supabase.from('ml_pagos')
      .select('id, ml_payment_id, ml_order_id, status, amount, currency_id, net_received_amount, money_release_date, money_released, date_approved, asiento_id')
      .order('date_created', { ascending: false }).limit(500)
    // Lookup manual comprador desde ml_ventas
    const orderIds = (pagosData || []).map(p => p.ml_order_id).filter(Boolean)
    let ventasMap = {}
    if (orderIds.length) {
      const { data: ventasData } = await supabase.from('ml_ventas')
        .select('ml_order_id, cliente_nickname, producto_id').in('ml_order_id', orderIds)
      for (const v of ventasData || []) ventasMap[v.ml_order_id] = v
    }
    setPagos((pagosData || []).map(p => ({ ...p, venta: ventasMap[p.ml_order_id] })))
    setLoading(false)
  }

  const kpis = useMemo(() => {
    const aprobados = pagos.filter(p => p.status === 'approved')
    const bruto = aprobados.reduce((s, p) => s + Number(p.amount || 0), 0)
    const neto = aprobados.reduce((s, p) => s + Number(p.net_received_amount || 0), 0)
    const porLiberar = aprobados.filter(p => !p.money_released).reduce((s, p) => s + Number(p.net_received_amount || 0), 0)
    const sinAsiento = aprobados.filter(p => p.money_released && !p.asiento_id).length
    return { total: pagos.length, aprobados: aprobados.length, bruto, neto, porLiberar, sinAsiento }
  }, [pagos])

  const filtrados = useMemo(() => {
    if (filtroStatus === 'todos') return pagos
    if (filtroStatus === 'sin_asiento') return pagos.filter(p => p.status === 'approved' && p.money_released && !p.asiento_id)
    if (filtroStatus === 'por_liberar') return pagos.filter(p => p.status === 'approved' && !p.money_released)
    return pagos.filter(p => p.status === filtroStatus)
  }, [pagos, filtroStatus])

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, color: '#1A3461', margin: 0 }}>Cobros MercadoLibre</h1>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>Pagos ML + liberación de dinero + asiento Zeta</div>
          </div>
          <button onClick={loadData} style={{ padding: '7px 14px', background: '#1A3461', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>↻ Actualizar</button>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          <KpiCard label="Pagos aprobados" valor={kpis.aprobados} sub={`de ${kpis.total} totales`} />
          <KpiCard label="Bruto cobrado" valor={fmt(kpis.bruto)} color="#1A3461" />
          <KpiCard label="Neto ML" valor={fmt(kpis.neto)} color="#10B981" sub={`comisión ~${kpis.bruto ? ((1 - kpis.neto/kpis.bruto)*100).toFixed(1) : 0}%`} />
          <KpiCard label="Por liberar" valor={fmt(kpis.porLiberar)} color="#F59E0B" />
          <KpiCard label="Sin asiento" valor={kpis.sinAsiento} color={kpis.sinAsiento > 0 ? '#EF4444' : '#94A3B8'} sub="requieren revisión" />
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            { v: 'todos', l: 'Todos' },
            { v: 'approved', l: 'Aprobados' },
            { v: 'por_liberar', l: 'Por liberar' },
            { v: 'sin_asiento', l: '⚠ Sin asiento' },
            { v: 'pending', l: 'Pendientes' },
            { v: 'rejected', l: 'Rechazados' },
            { v: 'refunded', l: 'Devueltos' },
          ].map(f => (
            <button key={f.v} onClick={() => setFiltroStatus(f.v)} style={{
              padding: '5px 11px', fontSize: 11, fontWeight: 700, borderRadius: 999, border: 'none',
              background: filtroStatus === f.v ? '#1A3461' : '#F1F5F9',
              color: filtroStatus === f.v ? '#fff' : '#475569', cursor: 'pointer',
            }}>{f.l}</button>
          ))}
        </div>

        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Cargando…</div> :
         filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', background: '#F8FAFC', borderRadius: 8 }}>
            {pagos.length === 0
              ? 'Todavía no llegó ningún pago ML. Cuando entre la primera orden pagada aparece acá.'
              : 'Ningún pago con este filtro.'}
          </div>
         ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
            <thead style={{ background: '#F8FAFC', color: '#475569', textAlign: 'left' }}>
              <tr>
                <th style={{ padding: '10px 8px' }}>Aprobado</th>
                <th>Payment · Order</th>
                <th>Comprador</th>
                <th style={{ textAlign: 'right' }}>Bruto</th>
                <th style={{ textAlign: 'right' }}>Neto</th>
                <th>Status</th>
                <th>Liberación</th>
                <th>Asiento</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => {
                const meta = STATUS_META[p.status] || { bg: '#F1F5F9', color: '#475569', label: p.status }
                const rel = diasHastaLiberacion(p.money_release_date)
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '8px 8px', color: '#64748B' }}>{fmtDate(p.date_approved)}</td>
                    <td>
                      <code style={{ fontSize: 10.5, background: '#F8FAFC', padding: '1px 5px', borderRadius: 3 }}>{p.ml_payment_id}</code>
                      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>order {p.ml_order_id}</div>
                    </td>
                    <td style={{ fontWeight: 700, color: '#1A3461' }}>{p.venta?.cliente_nickname || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(p.amount, p.currency_id)}</td>
                    <td style={{ textAlign: 'right', color: '#10B981', fontWeight: 700 }}>{fmt(p.net_received_amount, p.currency_id)}</td>
                    <td><span style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 999, background: meta.bg, color: meta.color, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase' }}>{meta.label}</span></td>
                    <td style={{ fontSize: 11 }}>
                      {p.money_released
                        ? <span style={{ color: '#10B981', fontWeight: 700 }}>✓ Liberado</span>
                        : p.money_release_date
                          ? <span style={{ color: '#F59E0B' }}>{rel}</span>
                          : <span style={{ color: '#94A3B8' }}>—</span>}
                    </td>
                    <td>
                      {p.asiento_id
                        ? <a href={`/admin/finanzas/asientos/${p.asiento_id}`} style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 800, textDecoration: 'none' }}>✓ Zeta</a>
                        : p.money_released && p.status === 'approved'
                          ? <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#FEE2E2', color: '#991B1B', fontWeight: 700 }}>⚠ Faltante</span>
                          : <span style={{ fontSize: 10.5, color: '#94A3B8' }}>—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
         )}
      </div>
    </AdminLayout>
  )
}

function KpiCard({ label, valor, sub, color = '#1A3461' }) {
  return (
    <div style={{ background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
      <div style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '.05em', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, color, fontWeight: 800, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}
